// Purpose: Hook to fetch and manage user role from Supabase user_roles table.

import { useState, useEffect, useCallback } from 'react';
import { User } from '@supabase/supabase-js';
import { UserRolesAPI } from '@/api';

export const useUserRole = (user: User | null) => {
  const [userRole, setUserRole] = useState<'admin' | 'client' | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isRoleLoading, setIsRoleLoading] = useState(true);

  const checkUserRole = useCallback(async () => {
    if (!user) {
      setUserRole(null);
      setIsAdmin(false);
      setIsRoleLoading(false);
      return;
    }
    try {
      setIsRoleLoading(true);
      console.log('Checking user role for:', user.id);
      const response = await UserRolesAPI.fetchUserRole(user.id);

      if (!response.success) {
        console.error('Error fetching user role:', response.error);
        setUserRole('client');
        setIsAdmin(false);
      } else if (response.data) {
        console.log('User role found:', response.data);
        setUserRole(response.data as 'admin' | 'client');
        setIsAdmin(response.data === 'admin');
      } else {
        console.log('No role found for user, defaulting to client');
        setUserRole('client');
        setIsAdmin(false);
      }
    } catch (error) {
      console.error('Error checking user role:', error);
      setUserRole('client');
      setIsAdmin(false);
    } finally {
      setIsRoleLoading(false);
    }
  }, [user]);

  useEffect(() => {
    checkUserRole();
  }, [user?.id, checkUserRole]);

  return {
    userRole,
    isAdmin,
    isRoleLoading,
    checkUserRole
  };
};
